/**
 * TestClaimMappingPanel (Sprint C12 Lane 4)
 *
 * Test harness for the IdP `/identity-providers/{id}/test-claim-mapping`
 * endpoint. An operator pastes sample IdP claims as JSON, the panel POSTs
 * them to the auth service, and the result -- mapped attributes, assigned
 * roles, missing required claims, unmapped claims -- is rendered as inline
 * chips and a key/value table.
 *
 * The endpoint is read-only; calling it has no side effects on the live
 * provider. It is the canonical way to validate an IdP configuration
 * before it is applied to real consumer logins.
 *
 * Composes only from `@aaimplatform/ui`. Uses the typed openapi-fetch
 * client factory `createIdentityProviderClient` so the request and
 * response shapes are enforced at compile time.
 *
 * @see schema-registry/identity-provider-registry.openapi.yaml#testClaimMapping
 */

import { useState } from 'react';
import { Button, Card, Textarea, Badge, BodyText, Heading, TableScroll } from '@aaimplatform/ui';
import { createIdentityProviderClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';
import { authMiddleware } from '../api/client';
import { formatApiError } from '../lib/api-error';

type TestClaimMappingResponse = components['schemas']['TestClaimMappingResponse'];

const DEFAULT_SAMPLE_CLAIMS = JSON.stringify(
  {
    sub: 'auth0|abc123',
    email: 'jane.doe@example.com',
    given_name: 'Jane',
    family_name: 'Doe',
    'custom:staff_role': 'loan_officer',
  },
  null,
  2,
);

export interface TestClaimMappingPanelProps {
  /**
   * The provider ID whose mapping configuration will be tested. When
   * undefined (e.g. the IdP has not been saved yet), the panel renders
   * a guidance message instead of the test interface.
   */
  providerId?: string;
  /** When true, all interaction is disabled. */
  disabled?: boolean;
}

interface TestResult {
  data: TestClaimMappingResponse['data'];
  providerName?: string;
}

export function TestClaimMappingPanel({ providerId, disabled = false }: TestClaimMappingPanelProps) {
  const [sampleClaimsText, setSampleClaimsText] = useState(DEFAULT_SAMPLE_CLAIMS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);
  const [expanded, setExpanded] = useState(false);

  const client = createIdentityProviderClient();
  // CSRF double-submit (N8) echo + auth-broken bounce on this mutating client.
  client.use(authMiddleware());

  if (!providerId) {
    return (
      <Card className="p-4">
        <BodyText size="sm" color="secondary">
          Save the provider first to enable test claim mapping. Once the
          provider exists, this panel can be used to validate the mapping
          configuration with sample claims.
        </BodyText>
      </Card>
    );
  }

  const handleTest = async () => {
    if (disabled || submitting) return;
    setError(null);
    setResult(null);

    let parsed: Record<string, unknown>;
    try {
      const candidate = JSON.parse(sampleClaimsText);
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        throw new Error('Sample claims must be a JSON object');
      }
      parsed = candidate as Record<string, unknown>;
    } catch (parseError) {
      setError(
        parseError instanceof Error
          ? `Invalid JSON: ${parseError.message}`
          : 'Invalid JSON',
      );
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: apiError, response } = await client.POST(
        '/api/v1/identity-providers/{providerId}/test-claim-mapping',
        {
          params: { path: { providerId } },
          body: {
            sampleClaims: parsed as Record<string, string | string[]>,
            protocol: 'oidc',
          },
        },
      );
      if (apiError) {
        setError(`Test failed: ${formatApiError(apiError, 'Test failed', response?.status)}`);
        return;
      }
      if (data && 'success' in data && data.success && 'data' in data) {
        setResult({ data: data.data, providerName: data.data?.providerName });
      } else {
        setError('Test failed: response missing data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-warm-50 transition-colors"
        aria-expanded={expanded}
      >
        <div className="text-left">
          <Heading level={3} size="card">
            Test Claim Mapping
          </Heading>
          <BodyText size="sm" color="secondary">
            Validate the configured mapping against sample IdP claims.
          </BodyText>
        </div>
        <span className="text-secondary text-sm">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-recessed pt-4 space-y-4">
          <div>
            <Textarea
              label="Sample Claims (JSON)"
              value={sampleClaimsText}
              onChange={(next) => setSampleClaimsText(next)}
              rows={10}
              disabled={disabled || submitting}
              helperText="Paste sample IdP claims as a JSON object. Values may be strings or arrays of strings."
            />
          </div>

          {error && (
            <div className="p-3 bg-error/10 border border-error/30 rounded-sm">
              <span className="text-sm text-error">{error}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={handleTest}
              disabled={disabled || submitting}
            >
              {submitting ? 'Testing...' : 'Test Mapping'}
            </Button>
          </div>

          {result?.data && (
            <div className="space-y-4 pt-2 border-t border-recessed">
              {result.providerName && (
                <BodyText size="sm" color="secondary">
                  Tested against: <span className="font-medium text-primary">{result.providerName}</span>
                </BodyText>
              )}

              {result.data.missingRequiredClaims && result.data.missingRequiredClaims.length > 0 && (
                <div>
                  <BodyText size="sm" color="primary" className="font-medium mb-1">
                    Missing required claims
                  </BodyText>
                  <div className="flex flex-wrap gap-2">
                    {result.data.missingRequiredClaims.map((claim: string) => (
                      <Badge key={`missing-${claim}`} variant="error">
                        {claim}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {result.data.unmappedClaims && result.data.unmappedClaims.length > 0 && (
                <div>
                  <BodyText size="sm" color="primary" className="font-medium mb-1">
                    Unmapped claims
                  </BodyText>
                  <div className="flex flex-wrap gap-2">
                    {result.data.unmappedClaims.map((claim: string) => (
                      <Badge key={`unmapped-${claim}`} variant="warning">
                        {claim}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {result.data.assignedRoles && result.data.assignedRoles.length > 0 && (
                <div>
                  <BodyText size="sm" color="primary" className="font-medium mb-1">
                    Assigned roles
                  </BodyText>
                  <div className="flex flex-wrap gap-2">
                    {result.data.assignedRoles.map((role: string, idx: number) => (
                      <Badge key={`role-${role}-${idx}`} variant="success">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {result.data.mappedAttributes &&
                Object.keys(result.data.mappedAttributes).length > 0 && (
                  <div>
                    <BodyText size="sm" color="primary" className="font-medium mb-1">
                      Mapped attributes
                    </BodyText>
                    <div className="border border-recessed rounded-sm overflow-hidden">
                      <TableScroll label="Mapped attributes">
                      <table className="w-full min-w-table-dense text-sm">
                        <tbody>
                          {Object.entries(result.data.mappedAttributes).map(
                            ([attribute, claimValue]) => (
                              <tr
                                key={attribute}
                                className="border-b border-recessed last:border-b-0"
                              >
                                <td className="px-3 py-2 font-mono text-xs text-secondary w-1/3">
                                  {attribute}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-primary">
                                  {Array.isArray(claimValue)
                                    ? claimValue.join(', ')
                                    : String(claimValue)}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                      </TableScroll>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
